import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useDashboardData } from '../useDashboardData';
import { TileHint } from '../TileFrame';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE } from '../dashboardTokens';
import { formatWallClock } from '../timeFormat';
import { FlowTileDetail } from './FlowTile';

// Schedule tile (#1160, specs/workshop-dashboard.md): where the day is
// going — the next steps with their wall-clock slots (breaks and lunches
// are ordinary chain steps, decision 2026-10-19) and the estimated end.
// Expanded view is the whole day plan — the same table the flow tile opens.

const NextList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 0.9vmin, 20px);
`;

const NextRow = styled.li`
  display: flex;
  align-items: baseline;
  gap: clamp(8px, 1vmin, 22px);
  font-size: ${DASH_TYPE.meta};

  time {
    font-family: ${DASH_FONTS.numeric};
    font-weight: 700;
    color: ${DASH_COLORS.accent};
    flex: 0 0 auto;
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EndEstimate = styled.div`
  margin-top: clamp(8px, 1.2vmin, 28px);
  font-size: ${DASH_TYPE.meta};
  color: ${DASH_COLORS.textDim};
`;

export function ScheduleTileBody({ t }) {
  const { language } = useTranslation();
  const { data } = useDashboardData();
  const flow = data?.flow;
  if (!flow) return <TileHint>{t('dashboard.noData')}</TileHint>;

  const liveIndex = flow.steps.findIndex((s) => s.isLive);
  const upcoming = flow.steps
    .slice(liveIndex >= 0 ? liveIndex + 1 : 0)
    .filter((s) => !s.reachedAt)
    .slice(0, 3);
  const end = formatWallClock(data.schedule?.estimatedEnd);

  return (
    <>
      <NextList>
        {upcoming.map((step) => (
          <NextRow key={step.stepId}>
            <time>{formatWallClock(step.schedule?.start) || '–'}</time>
            <span>{step.labels?.[language] || step.name || step.family}</span>
          </NextRow>
        ))}
      </NextList>
      {end && <EndEstimate>{t('dashboard.estimatedEnd', { time: end })}</EndEstimate>}
    </>
  );
}

// The day plan in full — shared with the flow tile.
export const ScheduleTileDetail = FlowTileDetail;
